import { FC, useState, useEffect } from 'react'
import Bugsnag from '@bugsnag/js'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { Store } from 'store/types'
import IntlTelInput, { CountryData } from '@andrewsantarin/react-intl-tel-input'
import '@andrewsantarin/react-intl-tel-input/dist/main.css'
import { SubscribeFormInputs } from 'features/simulation/types'
import { Email } from 'constants/regex'
import { updateSimulationInput } from 'store/simulation/actions/updateSimulationInput'
import { SimulationInputKeys } from 'store/simulation/types'
import { hasNoNumber, parseSubscribeData } from 'utils/helpers'
import { ContactData } from 'types/subscribe'
import { subscribe } from 'api/customerFlow'
import { subscribeError, subscribeStart, subscribeSuccess } from 'store/app/actions/subscribe'
import { useRouter } from 'next/router'
import LoadingDots from 'components/loading-dots/LoadingDots'
import classNames from 'classnames'
import styles from './Steps.module.scss'
import simulationStyles from '../Simulation.module.scss'
import Button from 'components/button/Button'
import { updateRegistrationInput } from 'store/registration/actions/updateRegistrationInputs'
import { InputGroupKeys, PersonalDataFieldKeys } from 'store/registration/types'
import { trackEvent } from 'utils/eventTracking'
import { Language } from 'types/language'
import { CountryCode } from 'libphonenumber-js/types'
import { PardotFormHandlers } from 'constants/constants'
import { SubscribeStepPropTypes as Props } from '../types'
import Icon from 'components/icon/Icon'

export const SubscribeStep: FC<Props> = ({ endSimulation }) => {
  // REDUX STORE
  const { app, marketing, simulation } = useSelector((store: Store) => store)
  const { isLoading, error } = app
  const { inputs, didSimulation } = simulation
  const { firstname, lastname, email, permission } = inputs
  const dispatch = useDispatch()

  // NEXT ROUTER
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['simulation', 'commonFormFields'])

  // REACT HOOK FORM
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<SubscribeFormInputs>({
    mode: 'onChange',
    defaultValues: { firstname, lastname, email, permission }
  })
  const watchFirstname = watch('firstname')
  const watchLastname = watch('lastname')
  const watchEmail = watch('email')
  const watchPermission = watch('permission')

  // STATE
  const [phone, setPhone] = useState('')
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryCode>('BE')
  const [phoneTouched, setPhoneTouched] = useState(false)
  const [validPhoneNumber, setValidPhoneNumber] = useState(false)

  /**
   * Handles the submit after validation
   * Subscribes the user
   *
   * @param data
   */
  const onSubmit = async (data: SubscribeFormInputs) => {
    dispatch(subscribeStart())

    try {
      const { firstname: firstName, lastname: lastName, email, permission } = data

      // Add user data to Bugsnag
      Bugsnag.addMetadata('user', {
        'first name': firstName,
        'last name': lastName,
        email,
        phone
      })

      // Update the fields in the simulation state
      dispatch(updateSimulationInput({ key: SimulationInputKeys.FIRSTNAME, value: firstName }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.LASTNAME, value: lastName }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.EMAIL, value: email }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.PHONE, value: phone }))
      dispatch(updateSimulationInput({ key: SimulationInputKeys.PERMISSION, value: permission }))

      // Update the fields in the registration state (firstname, lastname & email are also used there)
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.FIRSTNAME, value: firstName }))
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.LASTNAME, value: lastName }))
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.EMAIL, value: email }))
      dispatch(updateRegistrationInput({ group: InputGroupKeys.PERSONAL_DATA, key: PersonalDataFieldKeys.MOBILE_PHONE, value: phone }))

      // Create the contact data object
      const contactData: ContactData = { firstName, lastName, email, mobilePhone: phone, language: locale as Language }

      // Subscribe the lead
      const parsedSubscribeData = parseSubscribeData(
        contactData,
        simulation,
        marketing,
        phoneCountryCode,
        false,
        PardotFormHandlers.PRICING_SIMULATION
      )
      const opportunityId = await subscribe(parsedSubscribeData)

      if (opportunityId && opportunityId !== '') {
        dispatch(subscribeSuccess({ opportunityId }))

        // Track 'HotLead' event @Semetis
        trackEvent('HotLead')
      } else {
        dispatch(subscribeError())
      }
    } catch (e) {
      dispatch(subscribeError())

      // Report handled error to Bugsnag
      Bugsnag.notify(e)
    }
  }

  /**
   * Returns if submit button should be disabled
   */
  const isSubmitButtonDisabled = () => {
    return (
      watchFirstname === '' ||
      watchLastname === '' ||
      watchEmail === '' ||
      watchPermission === false ||
      !validPhoneNumber ||
      isLoading ||
      Object.values(errors).length > 0
    )
  }

  /**
   * On first render, if simulation is done, trigger 'Simulation Finished' event @Semetis
   */
  useEffect(() => {
    if (didSimulation) {
      // Destructure data from inputs for tracking
      const { deliveryPoint, hasVat, needsGas, usage, buildingType, residents, hasSolarPanels } = inputs

      // Track 'Simulation Finished' event @Semetis
      trackEvent('simulation_finished', {
        postcode: deliveryPoint.address.postalCode,
        costumer_type: hasVat ? 'company' : 'private',
        product_need: needsGas ? 'gaz + electricity' : 'electricity',
        consumption: usage,
        housing_type: buildingType,
        cohabitant_number: residents,
        solar_panel: hasSolarPanels,
        date: format(new Date(), 'dd/MM/yyyy')
      })
    }
  }, [])

  return (
    <div>
      <button className={styles['close-button']} onClick={() => endSimulation()}>
        <Icon name="close" color="#CBCBCF" />
      </button>
      <div className="is-flex-column is-flex-centered">
        <h2 className={classNames(styles.title, 'margin-top-none', 'margin-bottom-l')}>
          {t('subscribeFormTitle', 'Laat je gegevens achter...')}
        </h2>
      </div>
      <div className={styles['simulation-inputs']}>
        <form className={styles['last-step-subscribe-form']} onSubmit={handleSubmit(onSubmit)}>
          <div className="margin-bottom-l">
            <div className={styles['input-row']}>
              <div className={styles['input']}>
                <label htmlFor="firstname" className="is-block">
                  {t('commonFormFields:firstName', 'Voornaam')}
                </label>
                <input
                  type="text"
                  className={classNames('border', { 'has-error': errors.firstname })}
                  {...register('firstname', {
                    required: true,
                    minLength: 2,
                    maxLength: 40,
                    validate: (value: string) => hasNoNumber(value)
                  })}
                />
                {errors && errors.firstname && (
                  <span className="is-block margin-bottom-m is-red">
                    {errors.firstname.type === 'required' && t('validation:required')}
                    {errors.firstname.type === 'minLength' &&
                      t('validation:min.string', {
                        attribute: t('commonFormFields:firstName', 'Voornaam'),
                        length: 2
                      })}
                    {errors.firstname.type === 'maxLength' &&
                      t('validation:max.string', {
                        attribute: t('commonFormFields:firstName', 'Voornaam'),
                        length: 40
                      })}
                    {errors.firstname.type === 'validate' &&
                      t('validation:noNumbers', {
                        attribute: t('commonFormFields:firstName')
                      })}
                  </span>
                )}
              </div>

              <div className={styles['input']}>
                <label htmlFor="lastname" className="is-block">
                  {t('commonFormFields:lastName', 'Achternaam')}
                </label>
                <input
                  type="text"
                  className={classNames('border', { 'has-error': errors.lastname })}
                  {...register('lastname', {
                    required: true,
                    maxLength: 80,
                    validate: (value: string) => hasNoNumber(value)
                  })}
                />
                {errors && errors.lastname && (
                  <span className="is-block margin-bottom-m is-red">
                    {errors.lastname.type === 'required' && t('validation:required')}
                    {errors.lastname.type === 'maxLength' &&
                      t('validation:max.string', {
                        attribute: t('commonFormFields:lastName', 'Achternaam'),
                        length: 80
                      })}
                    {errors.lastname.type === 'validate' &&
                      t('validation:noNumbers', {
                        attribute: t('commonFormFields:lastName')
                      })}
                  </span>
                )}
              </div>
            </div>

            <div className={styles['input']}>
              <label htmlFor="email" className="is-block">
                {t('commonFormFields:email', 'E-mailadres')}
              </label>
              <input
                type="email"
                className={classNames('border', { 'has-error': errors.email })}
                {...register('email', { required: true, max: 255, pattern: Email })}
              />
              {errors && errors.email && (
                <span className="is-block margin-bottom-m is-red">{errors.email.type === 'required' && t('validation:required')}</span>
              )}
            </div>

            <div className={styles['input']}>
              <label htmlFor="simulationMobilePhone" className="is-block">
                {t('subscribeFormMobilePhoneRequired', 'Telefoonnummer')}
              </label>
              <IntlTelInput
                containerClassName="intl-tel-input is-fullwidth margin-bottom-m"
                inputClassName={`border ${phoneTouched && !validPhoneNumber ? 'has-error' : ''}`}
                preferredCountries={['be', 'nl', 'fr', 'de', 'lu']}
                value={phone}
                onPhoneNumberChange={(valid: boolean, value: string, countryData: CountryData) => {
                  setPhone(value)
                  setPhoneTouched(true)
                  setValidPhoneNumber(valid)
                  setPhoneCountryCode(countryData.iso2.toUpperCase() as CountryCode)
                }}
              />
            </div>

            <div>
              <label className="is-flex is-flex-row font-weight-regular font-weight-normal margin-bottom-m">
                <input type="checkbox" required {...register('permission', { required: true })} />
                {t('subscribeFormPermission', 'Ik geef Bolt toestemming om mij te contacteren.')}
              </label>
            </div>
          </div>

          <div className={simulationStyles['action-buttons']}>
            <Button isDisabled={isSubmitButtonDisabled()} extraClasses="margin-bottom-s" withGradient>
              {isLoading ? <LoadingDots /> : t('subscribeFormNext', 'Verzenden')}
            </Button>
          </div>

          {error && (
            <p className="is-red margin-top-m margin-bottom-none has-text-centered">{t('subscribeFormFailure', 'Er ging iets mis...')}</p>
          )}
        </form>
      </div>
    </div>
  )
}
